'use client';

import { useSearchParams, useRouter } from 'next/navigation';

import { Text } from '@backyard-ui/core';
import { z } from 'zod';

import { Pagination } from '@/presenters/components/common/Pagination';
import { MapLinkList, MapTitle } from '@/presenters/components/site-map';
import {
  useGetProductsMap,
  useGetProductsMetadata,
} from '@/presenters/hooks/site-map';

const validatePageParam = (value: string | null) =>
  z.coerce.number().int().gte(1).safeParse(value);
const PRODUCTS_SITEMAP_PATH = '/sitemap/produtos';

const getPageFromUrl = (url?: string) => {
  if (!url) return null;

  try {
    const page = new URL(url).searchParams.get('page');
    const parsedPage = Number.parseInt(page ?? '', 10);

    return parsedPage > 0 ? parsedPage : null;
  } catch {
    return null;
  }
};

export default function ProductsMap() {
  const searchParams = useSearchParams();
  const { replace } = useRouter();

  const pageParam = searchParams.get('page');

  const queryPageValidation = validatePageParam(pageParam);

  const currentPage = queryPageValidation.success
    ? queryPageValidation.data
    : 1;

  if (!queryPageValidation.success) {
    replace(`${PRODUCTS_SITEMAP_PATH}?page=1`);
  }

  const {
    data: productsMap,
    isFetching,
    isFetched,
  } = useGetProductsMap(currentPage);
  const { data: productsMetadata, isFetched: isMetadataFetched } =
    useGetProductsMetadata(currentPage);

  const previousPageFromMetadata = getPageFromUrl(productsMetadata?.data.prev);
  const nextPageFromMetadata = getPageFromUrl(productsMetadata?.data.next);
  const hasMetadataNavigation = Boolean(
    previousPageFromMetadata || nextPageFromMetadata
  );

  if (
    productsMap &&
    isMetadataFetched &&
    !hasMetadataNavigation &&
    productsMap.page > productsMap.pages
  ) {
    replace(`${PRODUCTS_SITEMAP_PATH}?page=1`);
  }

  if (isFetching || !isFetched) {
    return (
      <>
        <MapTitle>Mapa do Site - Produtos</MapTitle>

        <div className="mt-4 pl-6 text-center">
          <Text>Carregando...</Text>
        </div>
      </>
    );
  }

  if (!productsMap?.data.length) {
    return (
      <>
        <MapTitle>Mapa do Site - Produtos</MapTitle>

        <div className="mt-4 pl-6 text-center">
          <Text>Serviço indisponível</Text>
        </div>
      </>
    );
  }

  const effectiveCurrentPage = currentPage;
  const effectiveTotalPages = (() => {
    const totalPagesFromMap = productsMap.pages || currentPage;

    if (nextPageFromMetadata && totalPagesFromMap <= currentPage) {
      return currentPage + 1;
    }

    return Math.max(totalPagesFromMap, currentPage);
  })();

  return (
    <>
      <MapTitle>Mapa do Site - Produtos</MapTitle>

      <MapLinkList items={productsMap.data} />

      <Pagination
        currentPage={effectiveCurrentPage}
        totalPages={effectiveTotalPages}
        pathname={PRODUCTS_SITEMAP_PATH}
      />
    </>
  );
}
